import React, { useEffect, useState, useMemo } from 'react';
import { Tabs, Tab, Table, Button, Form, Spinner, Row, Col, Card, Badge, Modal, InputGroup, Pagination } from 'react-bootstrap';
import axios from 'axios';
import { 
  FaBriefcase, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaSearch, 
  FaTrashAlt, 
  FaEdit, 
  FaPlus, 
  FaCheck, 
  FaTimes, 
  FaLayerGroup, 
  FaDollarSign, 
  FaFilter,
  FaBuilding,
  FaChartLine,
  FaUndo
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';

const JobManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newJob, setNewJob] = useState({
    title: '',
    category_name: '',
    type: 'Featured',
    company_name: '',
    location: '',
    salary: '',
    description: '',
    image: ''
  });
  const [activeTab, setActiveTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Advanced Filter States
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://api.collegeall.in/api/v1/jobs');
      setJobs(response.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get('https://api.collegeall.in/api/v1/get-categories');
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Centralized filtering logic
  useEffect(() => {
    let result = [...jobs];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(job => 
        job.title.toLowerCase().includes(term) ||
        job.company_name.toLowerCase().includes(term) ||
        job.location.toLowerCase().includes(term) ||
        job.category_name.toLowerCase().includes(term)
      );
    }

    if (filterCategory) {
      result = result.filter(job => job.category_name === filterCategory);
    }

    if (filterType) {
      result = result.filter(job => job.type === filterType);
    }

    if (filterLocation) {
      result = result.filter(job => job.location === filterLocation);
    }

    setFilteredJobs(result);
    setCurrentPage(1); // Reset to first page on filter change
  }, [jobs, searchTerm, filterCategory, filterType, filterLocation]);

  const resetFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterType('');
    setFilterLocation('');
  };

  const locations = useMemo(() => {
    const uniqueLocations = [...new Set(jobs.map(job => job.location).filter(Boolean))];
    return uniqueLocations.sort();
  }, [jobs]);

  const handleAddJob = async (e) => {
    e.preventDefault();
    try {
      const endpoint = editingJob 
        ? `https://api.collegeall.in/api/v1/update-job/${editingJob.id}`
        : 'https://api.collegeall.in/api/v1/add-job';
      
      const method = editingJob ? 'put' : 'post';
      
      const res = await axios[method](endpoint, newJob);
      toast.success(res.data.message);
      fetchJobs();
      
      setNewJob({ 
        title: '', 
        category_name: '', 
        type: 'Featured', 
        company_name: '', 
        location: '', 
        salary: '', 
        description: '', 
        image: '' 
      });
      
      setEditingJob(null);
      setActiveTab('list');
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error(`Failed to ${editingJob ? 'update' : 'add'} job`);
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setNewJob({
      title: job.title,
      category_name: job.category_name,
      type: job.type,
      company_name: job.company_name,
      location: job.location,
      salary: job.salary,
      description: job.description,
      image: job.image
    });
    setActiveTab('add');
  };

  const handleDeleteJob = async () => {
    try {
      await axios.delete(`https://api.collegeall.in/api/v1/delete-job/${jobToDelete.id}`);
      toast.success('Job deleted successfully');
      fetchJobs();
      setShowDeleteModal(false);
      setJobToDelete(null);
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const confirmDelete = (job) => {
    setJobToDelete(job);
    setShowDeleteModal(true);
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Category name cannot be empty');
      return;
    }
    
    try {
      await axios.post('https://api.collegeall.in/api/v1/add-category', { category: newCategory });
      toast.success('Category added successfully');
      setNewCategory('');
      setShowCategoryForm(false);
      fetchCategories();
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchCategories();
  }, []);

  // Pagination Logic
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  const renderPaginationItems = () => {
    const items = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
      }
      return items;
    }

    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start <= 1) {
      start = 1;
      end = 3;
    } else if (end >= totalPages) {
      end = totalPages;
      start = totalPages - 2;
    }

    if (start > 1) {
      items.push(<Pagination.Ellipsis key="start-el" disabled />);
    }

    for (let i = start; i <= end; i++) {
      if (i < totalPages) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
    }

    if (end < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-el" disabled />);
    }

    items.push(
      <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
        {totalPages}
      </Pagination.Item>
    );

    return items;
  };

  const stats = [
    { title: 'Total Active', count: jobs.length, icon: <FaBriefcase />, color: '#6366f1' },
    { title: 'Featured', count: jobs.filter(j => j.type === 'Featured').length, icon: <FaChartLine />, color: '#10b981' },
    { title: 'Categories', count: categories.length, icon: <FaLayerGroup />, color: '#f59e0b' },
    { title: 'Posted Today', count: jobs.filter(j => moment(j.posted_date).isSame(moment(), 'day')).length, icon: <FaCalendarAlt />, color: '#ec4899' },
  ];

  return (
    <div className="job-mgmt-container p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Career Management</h2>
          <p className="text-muted small mb-0">List, monitor and update available opportunities</p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => {
            setEditingJob(null);
            setNewJob({ title: '', category_name: '', type: 'Featured', company_name: '', location: '', salary: '', description: '', image: '' });
            setActiveTab('add');
          }}
          className="btn-modern shadow-sm"
        >
          <FaPlus className="me-2" /> Add New Job
        </Button>
      </div>

      {/* Analytics Row */}
      <Row className="g-3 mb-4">
        {stats.map((stat, i) => (
          <Col md={3} key={i}>
            <Card className="border-0 shadow-sm metric-card p-3">
              <div className="d-flex align-items-center">
                <div className="metric-icon" style={{ color: stat.color, background: `${stat.color}15` }}>
                  {stat.icon}
                </div>
                <div className="ms-3">
                  <div className="small text-muted fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>{stat.title}</div>
                  <h4 className="fw-bold mb-0">{stat.count}</h4>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <Tabs 
            activeKey={activeTab} 
            onSelect={(k) => {
              setActiveTab(k);
              if (k === 'list') setEditingJob(null);
            }}
            className="modern-tabs border-0"
          >
            <Tab eventKey="list" title="Active Listings" />
            <Tab eventKey="add" title={editingJob ? "Modify Listing" : "New Listing"} />
          </Tabs>

          {activeTab === 'list' && (
            <div className="d-flex gap-2">
              <InputGroup className="search-group">
                <InputGroup.Text className="bg-light border-0">
                  <FaSearch className="text-muted small" />
                </InputGroup.Text>
                <Form.Control
                  className="bg-light border-0 small shadow-none"
                  placeholder="Search jobs, companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Button 
                variant={showFilters ? "primary" : "light"} 
                className={`border shadow-none btn-icon ${showFilters ? 'text-white' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter />
              </Button>
            </div>
          )}
        </div>

        {/* Dynamic Filter Bar */}
        {activeTab === 'list' && showFilters && (
          <div className="px-3 py-2 bg-light-subtle border-bottom animate-fade-in">
            <Row className="g-2 align-items-center">
              <Col md={3}>
                <Form.Select 
                  size="sm" 
                  className="border-0 shadow-sm"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat, i) => (
                    <option key={i} value={cat.job_category}>{cat.job_category}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  size="sm" 
                  className="border-0 shadow-sm"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Featured">Featured Only</option>
                  <option value="Recent">Recent Only</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  size="sm" 
                  className="border-0 shadow-sm"
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {locations.map((loc, i) => (
                    <option key={i} value={loc}>{loc}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button variant="link" className="text-muted small p-0 text-decoration-none" onClick={resetFilters}>
                  <FaUndo className="me-1 x-small" /> Reset Filters
                </Button>
              </Col>
            </Row>
          </div>
        )}

        <div className="tab-content">
          {activeTab === 'list' ? (
            <>
              <div className="table-responsive">
                <Table hover className="modern-table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th className="ps-4">Job Details</th>
                      <th>Category</th>
                      <th>Salary</th>
                      <th>Company</th>
                      <th>Posted On</th>
                      <th className="text-end pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <Spinner animation="border" size="sm" variant="primary" />
                        </td>
                      </tr>
                    ) : currentJobs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          <div className="mb-2"><FaBriefcase size={30} className="text-light-emphasis" /></div>
                          No jobs found matching your criteria.
                        </td>
                      </tr>
                    ) : (
                      currentJobs.map((job) => (
                        <tr key={job.id} className="job-row">
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <div className="job-type-indicator me-3" style={{ background: job.type === 'Featured' ? '#6366f1' : '#cbd5e1' }}></div>
                              <div>
                                <div className="fw-bold text-dark mb-0">{job.title}</div>
                                <div className="x-small text-muted d-flex align-items-center">
                                  <FaMapMarkerAlt className="me-1 text-danger x-small" /> {job.location}
                                  <span className="mx-2">•</span>
                                  <span className={job.type === 'Featured' ? 'text-primary fw-bold' : ''}>{job.type}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="border px-2 py-1 fw-medium rounded-pill x-small">
                              {job.category_name}
                            </Badge>
                          </td>
                          <td>
                            <div className="fw-bold text-success small d-flex align-items-center">
                              <FaDollarSign className="me-1" /> {job.salary || 'Competitive'}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center small fw-medium">
                              <div className="company-logo-mini me-2">
                                {job.image ? <img src={job.image} alt="logo" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='block' }} /> : <FaBuilding />}
                                <FaBuilding className="hidden-alt" style={{ display: 'none' }} />
                              </div>
                              {job.company_name}
                            </div>
                          </td>
                          <td>
                            <div className="text-muted x-small d-flex align-items-center">
                              <FaCalendarAlt className="me-1" />
                              {moment(job.posted_date).format('DD MMM YYYY')}
                            </div>
                          </td>
                          <td className="text-end pe-4">
                            <div className="d-flex justify-content-end gap-2">
                              <Button 
                                variant="light" 
                                size="sm" 
                                className="btn-icon text-primary shadow-none"
                                onClick={() => handleEditJob(job)}
                              >
                                <FaEdit />
                              </Button>
                              <Button 
                                variant="light" 
                                size="sm" 
                                className="btn-icon text-danger shadow-none"
                                onClick={() => confirmDelete(job)}
                              >
                                <FaTrashAlt />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="p-3 bg-light border-top d-flex justify-content-between align-items-center">
                  <div className="small text-muted">
                    Showing {indexOfFirstJob + 1} to {Math.min(indexOfLastJob, filteredJobs.length)} of {filteredJobs.length} listings
                  </div>
                  <Pagination className="mb-0 modern-pagination">
                    <Pagination.Prev onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
                    {renderPaginationItems()}
                    <Pagination.Next onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="p-4 bg-white">
              <Form onSubmit={handleAddJob}>
                <Row className="g-4">
                  <Col md={8}>
                    <div className="section-label mb-3">Primary Information</div>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Job Title</Form.Label>
                          <Form.Control 
                            className="bg-light border-0 shadow-none" 
                            required 
                            placeholder="e.g. Senior Frontend Engineer"
                            value={newJob.title}
                            onChange={(e) => setNewJob({...newJob, title: e.target.value})}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase d-flex justify-content-between">
                            Category 
                            <span 
                              className="text-primary cursor-pointer" 
                              onClick={() => { setShowCategoryForm(!showCategoryForm); }}
                              style={{ textTransform: 'none', letterSpacing: 0 }}
                            >
                              {showCategoryForm ? 'Cancel' : '+ New Category'}
                            </span>
                          </Form.Label>
                          {showCategoryForm ? (
                            <InputGroup>
                              <Form.Control
                                className="bg-light border-0 shadow-none"
                                placeholder="Enter category"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                              />
                              <Button variant="success" className="border-0" onClick={handleAddCategory}><FaCheck /></Button>
                            </InputGroup>
                          ) : (
                            <Form.Select 
                              className="bg-light border-0 shadow-none"
                              required
                              value={newJob.category_name}
                              onChange={(e) => setNewJob({...newJob, category_name: e.target.value})}
                            >
                              <option value="">Select Category</option>
                              {categories.map((cat, index) => (
                                <option key={index} value={cat.job_category}>{cat.job_category}</option>
                              ))}
                            </Form.Select>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Placement Type</Form.Label>
                          <Form.Select
                            className="bg-light border-0 shadow-none"
                            required
                            value={newJob.type}
                            onChange={(e) => setNewJob({...newJob, type: e.target.value})}
                          >
                            <option value="Featured">Featured</option>
                            <option value="Recent">Recent</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Description</Form.Label>
                          <Form.Control 
                            as="textarea" 
                            rows={6} 
                            className="bg-light border-0 shadow-none" 
                            required 
                            placeholder="Detail the responsibilities and requirements..."
                            value={newJob.description}
                            onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>

                  <Col md={4}>
                    <div className="section-label mb-3">Organization Details</div>
                    <div className="p-3 border rounded-3 bg-light-subtle">
                      <Row className="g-3">
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Company Name</Form.Label>
                            <Form.Control 
                              className="bg-white border shadow-none" 
                              required 
                              placeholder="e.g. Acme Corp"
                              value={newJob.company_name}
                              onChange={(e) => setNewJob({...newJob, company_name: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Location</Form.Label>
                            <Form.Control 
                              className="bg-white border shadow-none" 
                              required 
                              placeholder="e.g. Remote / Bangalore"
                              value={newJob.location}
                              onChange={(e) => setNewJob({...newJob, location: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Compensation</Form.Label>
                            <Form.Control 
                              className="bg-white border shadow-none" 
                              placeholder="e.g. 12L - 18L PA"
                              value={newJob.salary}
                              onChange={(e) => setNewJob({...newJob, salary: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Brand Assets (Logo URL)</Form.Label>
                            <Form.Control 
                              className="bg-white border shadow-none" 
                              placeholder="https://..."
                              value={newJob.image}
                              onChange={(e) => setNewJob({...newJob, image: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    <div className="mt-4 d-grid gap-2">
                      <Button variant="primary" type="submit" className="fw-bold py-2 shadow-sm">
                        {editingJob ? <><FaEdit className="me-2" /> UPDATE LISTING</> : <><FaCheck className="me-2" /> PUBLISH JOB</>}
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        className="fw-bold border-0"
                        onClick={() => {
                          setActiveTab('list');
                          setEditingJob(null);
                        }}
                      >
                        CANCEL
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </div>
      </Card>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="confirm-delete-modal">
        <Modal.Body className="text-center p-4">
          <div className="mb-3 text-danger"><FaTrashAlt size={40} /></div>
          <h5 className="fw-bold">Archive Job Listing?</h5>
          <p className="text-muted small">Are you sure you want to delete <strong>{jobToDelete?.title}</strong>? This action cannot be undone.</p>
          <div className="d-grid gap-2">
            <Button variant="danger" onClick={handleDeleteJob}>
              YES, DELETE PERMANENTLY
            </Button>
            <Button variant="light" onClick={() => setShowDeleteModal(false)}>CANCEL</Button>
          </div>
        </Modal.Body>
      </Modal>
      
      <style>{`
        .job-mgmt-container {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }

        .metric-card {
          border-radius: 12px;
          transition: transform 0.2s ease;
        }
        
        .metric-card:hover {
          transform: translateY(-2px);
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .btn-modern {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          font-size: 0.85rem;
        }

        .modern-tabs .nav-link {
          color: #64748b;
          border: 0;
          font-size: 0.85rem;
          font-weight: 600;
          padding: 1rem 1.5rem;
          position: relative;
        }

        .modern-tabs .nav-link.active {
          color: #6366f1;
          background: transparent;
        }

        .modern-tabs .nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #6366f1;
        }

        .search-group {
          max-width: 280px;
        }

        .search-group .form-control {
          font-size: 0.85rem;
        }

        .btn-icon {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }

        .modern-table thead th {
          background: #f8fafc;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          border-top: 0;
          font-weight: 700;
        }

        .job-row:hover {
          background-color: #f1f5f9;
        }

        .job-type-indicator {
          width: 4px;
          height: 32px;
          border-radius: 4px;
        }

        .company-logo-mini {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          color: #64748b;
        }

        .company-logo-mini img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .modern-pagination .page-link {
          border: 0;
          background: transparent;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 600;
          margin: 0 2px;
          border-radius: 6px;
        }

        .modern-pagination .page-item.active .page-link {
          background: #6366f1;
          color: white;
        }

        .section-label {
          font-size: 0.7rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #94a3b8;
        }

        .x-small { font-size: 0.75rem; }
        .cursor-pointer { cursor: pointer; }
        .tracking-wider { letter-spacing: 0.05em; }

        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .confirm-delete-modal .modal-content {
          border-radius: 1.25rem;
          border: 0;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default JobManagement;
